import React from 'react';
import { Experiment } from '@printdeal/feature-management/react';
import clsx from 'clsx';
import type { NavigationContainer } from './types';

type NavigationExperimentProps<As extends React.ElementType> = {
  as: As;
  navigation: NavigationContainer['navigation'];
  className?: string;
} & Omit<React.ComponentPropsWithoutRef<As>, 'navigation' | 'className'>;

const NavigationExperiment = <As extends React.ElementType>({
  as,
  navigation,
  className,
}: NavigationExperimentProps<As>) => {
  // Help TypeScript understand the type of as.
  const Component: React.ElementType = as;

  // Narrow down the type of navigation to either Navigation or NavigationExperiment
  // based on experimentId property.
  if (!('experimentId' in navigation)) {
    // Return Component if navigation is of type Navigation.
    return (
      <Component navigation={navigation} className={className} />
    );
  }

  // Return Experiment if navigation is of type NavigationExperiment.
  return (
    <Experiment id={navigation.experimentId as string}>
      <Experiment.Control>
        {({ isInitialized }) => (
          <Component
            navigation={navigation.control}
            className={clsx(
              // Blurred control when initializing.
              !isInitialized && 'pd-blur-sm',
              className,
            )}
          />
        )}
      </Experiment.Control>
      <Experiment.Variant>
        <Component
          navigation={navigation.variant}
          className={className}
        />
      </Experiment.Variant>
    </Experiment>
  );
};

export default NavigationExperiment;
